/**
 * Router helpers, moved out of Glue's @bibliocommons/utils-routing under CORE-79046. Copied as it
 * stood: `useRouter`, `withRouter`, `withOptionalRouter`, `useQuery` and the two query-string
 * helpers behave exactly as they did there, deliberately.
 *
 * In particular `searchToQuery` / `queryToSearch` were NOT rewritten on URLSearchParams. The two
 * differ in ways that fail silently -- repeated keys collapse to the last value when read, and this
 * implementation drops empty, null, zero and false values on write where URLSearchParams emits them
 * (including the literal text "null"). Measurements are on CORE-77691. Any adoption of
 * useSearchParams should be per call site and verified, never a sweep.
 */
import React from 'react';
import PropTypes from 'prop-types';
import pickBy from 'lodash/pickBy';
import queryString from 'querystring';
import getDisplayName from 'react-display-name';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { useLocation, useParams, useNavigate } from 'react-router';

/**
 * Converts a location search string into an object of query string parameters.
 * @param {string} search
 * @return {object}
 */
export function searchToQuery(search) {
  if (!search) return {};
  const str = search.indexOf('?') === 0 ? search.slice(1) : search;
  return queryString.parse(str);
}

/**
 * Converts an object of query string parameters into a location search string.
 * NOTE: It removes empty and falsy values from the generated string.
 * @param {object} query
 * @return {string}
 */
export function queryToSearch(query) {
  const normalizedQuery = pickBy(query, val => (Array.isArray(val) ? val.length > 0 : !!val));
  const str = queryString.stringify(normalizedQuery);
  return str ? `?${str}` : '';
}

/**
 * React hook for accessing the current location query string object.
 * @return {object}
 */
export function useQuery() {
  return searchToQuery(useLocation().search);
}

/**
 * React hook for accessing a custom router object, with various location functions and properties.
 * To be used for navigation, generating urls and accessing location parameters.
 * @return {object}
 */
export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const query = useQuery();

  // react-router 6's useNavigate ignores any call made before its own layout effect has run --
  // "the navigate is useless because we haven't wired up our history listener yet" -- returning
  // silently, with only a dev warning. Layout effects commit bottom-up, so a class component
  // calling router.replace() from componentDidMount runs BEFORE the withRouter wrapper above it
  // becomes active, and the navigation is dropped. The same call worked under react-router 5, so
  // the failure is silent and version-specific.
  //
  // Hold the last such call and flush it from a passive effect. It must NOT be a layout effect:
  // the Router subscribes to history in a layout effect of its own, and ancestors' layout effects
  // run AFTER descendants', so navigating from one here changes the history while nothing is yet
  // listening -- verified, the location updates and React never re-renders. useEffect runs after
  // every layout effect in the tree, by which point the Router is subscribed.
  const isNavigateReadyRef = React.useRef(false);
  const pendingNavigationRef = React.useRef(null);

  React.useEffect(() => {
    isNavigateReadyRef.current = true;

    if (pendingNavigationRef.current) {
      const [to, options] = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      navigate(to, options);
    }
  });

  function createRouter() {
    // Built by hand rather than via useHref: that is a hook, so it cannot be called from inside
    // createHref(to), which takes its argument at call time. The only thing react-router 5's
    // history.createHref added over concatenation was the basename, and no consumer sets one.
    //
    // history v4's createPath also normalised a missing `?` or `#` on a raw search/hash; plain
    // concatenation does not. That is safe only because of how this is called: every call site
    // passes `{ pathname }` and/or `{ query }`, and queryToSearch returns '' or '?...'. Pass a
    // bare `search: 'a=1'` and you get `/patha=1`, so prefix it or use `query`.
    function createHref(to) {
      const pathname = to.pathname || location.pathname;
      const search = to.query ? queryToSearch(to.query) : to.search || '';
      return `${pathname}${search}${to.hash || ''}`;
    }

    function go(to, options) {
      const href = typeof to === 'string' ? to : createHref(to);

      if (!isNavigateReadyRef.current) {
        pendingNavigationRef.current = [href, options];
        return undefined;
      }

      return navigate(href, options);
    }

    return {
      location,
      params,
      query,
      push: to => go(to),
      replace: to => go(to, { replace: true }),
      createHref: to => createHref(to)
    };
  }

  // Add logic to return a new router object only when the location has changed.
  // This would allow components to rely on reference equality, to also determine
  // whether the location has changed.
  // NOTE: We're checking `location.key` here because we need to ignore changes
  // to the `location` object that do not come from react-router. For example,
  // the Modal component uses window.history.pushState to handle closing with the
  // browser back button, but that should not be considered a router transition.
  // The `location.key` property is only updated by react-router during transition.
  const routerRef = React.useRef(createRouter());
  if (routerRef.current.location.key !== location.key) {
    routerRef.current = createRouter();
  }

  return routerRef.current;
}

// Indirection for `withRouter` below to call through, so a test can mock `useRouter` there without
// mocking the module's own binding of it. In Glue this was `exports.useRouter()`, relying on the
// package's compiled-CommonJS output making `exports` a real, mutable object. This file stays ESM
// under webpack, where `exports` is either undefined (client) or not callable this way (server) --
// confirmed against both production bundles. A plain object with the same indirection works in both
// module systems.
export const routerHooks = { useRouter };

/**
 * HOC for accessing the custom router object.
 * @return {Component}
 */
export function withRouter(WrappedComponent) {
  const Wrapper = React.forwardRef((props, ref) => {
    // Use routerHooks.useRouter(), not the bare useRouter import, to support mocking the router
    // in tests.
    const router = routerHooks.useRouter();
    return <WrappedComponent {...props} ref={ref} router={router} />;
  });

  Wrapper.WrappedComponent = WrappedComponent;
  Wrapper.displayName = `withRouter(${getDisplayName(WrappedComponent)})`;

  return hoistNonReactStatics(Wrapper, WrappedComponent);
}

/**
 * HOC similar to `withRouter`, except the router may be optionally passed.
 * To omit the router simply pass `router={false}` to the wrapper component.
 * @return {Component}
 */
export function withOptionalRouter(WrappedComponent) {
  const WithRouterComponent = withRouter(WrappedComponent);

  const OptionalRouterComponent = React.forwardRef(({ router = true, ...props }, ref) => {
    const Component = router ? WithRouterComponent : WrappedComponent;
    return <Component {...props} ref={ref} />;
  });

  OptionalRouterComponent.propTypes = {
    router: PropTypes.bool
  };

  OptionalRouterComponent.WrappedComponent = WrappedComponent;
  OptionalRouterComponent.displayName = `withOptionalRouter(${getDisplayName(WrappedComponent)})`;

  return hoistNonReactStatics(OptionalRouterComponent, WrappedComponent);
}
